import AppSignal from '@appsignal/javascript';
import { plugin as breadcrumbsConsolePlugin } from '@appsignal/plugin-breadcrumbs-console';
import { plugin as breadcrumbsNetworkPlugin } from '@appsignal/plugin-breadcrumbs-network';
import { plugin as pathDecoratorPlugin } from '@appsignal/plugin-path-decorator';
import { plugin as windowEventsPlugin } from '@appsignal/plugin-window-events';

import { shouldRenameError } from '@/utils/errorNaming';
import { isServer } from '@/utils/helpers';
import { release } from '@/ZmyleReleaseInfo.js';

import { ClientConfig } from './clientConfig';

interface SendErrorParams {
  error: Error;
  payload: Record<string, any>;
  tags?: Record<string, any>;
}

export class AppSignalClient {
  private appSignal: AppSignal;

  private static instance: AppSignalClient;

  private static builtFromPopulatedConfig = false;

  private static hasPatchedWindowOnError = false;

  private constructor() {
    const clientConfig = ClientConfig.getInstance().getConfig();

    this.appSignal = new AppSignal({
      key: clientConfig.appSignalKey,
      revision: release ?? undefined,
    });

    this.appSignal.addDecorator(span => {
      if (isServer()) {
        span.setTags({ source: 'server' });
      } else {
        span.setTags({ source: 'browser', full_url: window.location.href });
      }

      return span;
    });

    if (typeof window === 'undefined') return;

    this.appSignal.use(breadcrumbsNetworkPlugin());
    this.appSignal.use(breadcrumbsConsolePlugin());
    this.appSignal.use(windowEventsPlugin());
    this.appSignal.use(pathDecoratorPlugin());

    if (AppSignalClient.hasPatchedWindowOnError) return;

    const bindOnError = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      if (error && shouldRenameError(error)) {
        error.name = error.message;
      }

      if (bindOnError) bindOnError(message, source, lineno, colno, error);
    };

    AppSignalClient.hasPatchedWindowOnError = true;
  }

  public static init(): void {
    AppSignalClient.instance = new AppSignalClient();

    AppSignalClient.builtFromPopulatedConfig = ClientConfig.isPopulated();
  }

  public static getInstance(): AppSignalClient {
    const wasBuiltBeforeConfigArrived =
      !AppSignalClient.builtFromPopulatedConfig && ClientConfig.isPopulated();

    if (!AppSignalClient.instance || wasBuiltBeforeConfigArrived) {
      AppSignalClient.init();
    }

    return AppSignalClient.instance;
  }

  public getClient() {
    return this.appSignal;
  }

  public sendError(params: SendErrorParams) {
    const { error, payload, tags = null } = params;

    const span = this.appSignal.createSpan();

    if (shouldRenameError(error)) error.name = error.message;

    span.setError(error);
    span.setParams(payload);

    if (tags) span.setTags(tags);

    this.appSignal.send(span);
  }
}
